import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Represents a data source with its metadata */
interface DataSource {
  /** Unique identifier for the data source */
  source: string;
  /** Whether this is a user-created custom source */
  isCustom?: boolean;
  /** Additional properties that might exist on the source */
  [key: string]: unknown;
}

/** Props for the DataSourceChip component */
interface DataSourceChipProps {
  /** The data source object */
  source: DataSource;
  /** Display name for the chip */
  displayName: string;
  /** Whether this chip is currently selected */
  isSelected: boolean;
  /** Handler to toggle selection state */
  onToggle: () => void;
  /** Handler to edit the data source (optional - only for custom sources) */
  onEdit?: () => void;
  /** Handler to delete the data source (optional - only for custom sources) */
  onDelete?: () => void;
  /**
   * Handler to configure the data source (optional - for sources needing
   * configuration)
   */
  onConfigure?: () => void;
  /** Whether this source needs configuration before use */
  needsConfiguration?: boolean;
  /** Optional children to render inside the chip (e.g., icons) */
  children?: React.ReactNode;
}

/**
 * DataSourceChip - A wrapper component for data source chips Handles all the
 * conditional logic for different chip states and types
 */
const DataSourceChip: React.FC<DataSourceChipProps> = ({
  source,
  displayName,
  isSelected,
  onToggle,
  onEdit,
  onConfigure,
  onDelete,
  needsConfiguration = false,
  children,
}) => {
  let handleClick: (() => void) | undefined;
  let handleEdit: (() => void) | undefined;
  let handleDelete: (() => void) | undefined;

  if (isSelected) {
    if (
      (source.source === 'Files' || source.source === 'Layers') &&
      onDelete
    ) {
      handleDelete = onDelete;
      handleClick = onToggle;
    } else {
      handleDelete = onToggle;
    }
    if (source.source === 'Incident' && onEdit) {
      handleEdit = onEdit;
    }
  } else {
    handleClick = needsConfiguration && onConfigure ? onConfigure : onToggle;
    if (source.isCustom) {
      if (onEdit) handleEdit = onEdit;
      if (onDelete) handleDelete = onDelete;
    }
  }

  return (
    <span
      className={cn(
        'tw:inline-flex tw:max-w-full tw:items-center tw:gap-0.5 tw:rounded-full tw:border tw:border-border tw:bg-muted tw:text-foreground',
        isSelected && 'tw:bg-muted tw:text-foreground'
      )}
    >
      <button
        type="button"
        className={cn(
          'tw:min-w-0 tw:rounded-full tw:px-3 tw:py-1.5 tw:text-left tw:text-xs tw:font-medium tw:whitespace-normal tw:text-foreground',
          handleClick && 'tw:cursor-pointer tw:hover:bg-accent/60'
        )}
        onClick={handleClick}
      >
        {children}
        {displayName}
      </button>
      {handleEdit ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="tw:mr-0.5 tw:text-muted-foreground"
          aria-label={`Edit ${displayName}`}
          onClick={(event) => {
            event.stopPropagation();
            handleEdit?.();
          }}
        >
          <Pencil />
        </Button>
      ) : null}
      {handleDelete ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="tw:mr-0.5 tw:text-muted-foreground"
          aria-label={`Remove ${displayName}`}
          onClick={(event) => {
            event.stopPropagation();
            handleDelete?.();
          }}
        >
          <Trash2 />
        </Button>
      ) : null}
    </span>
  );
};

export default DataSourceChip;
